import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Send, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fieldErrors } from "@/lib/api";
import { hoursBetween, cleanNum } from "@/lib/format";
import { Field, NativeSelect, LoadingBlock, Spinner, StatusBadge } from "@/components/Shared";
import FootageInputs from "@/components/FootageInputs";
import PhotoUploader from "@/components/PhotoUploader";
import CrewPicker from "@/components/CrewPicker";

const TEXT_KEYS = ["report_date", "client_id", "project_id", "contractor", "pipe_description", "weather", "start_time", "end_time", "comments", "issues", "safety", "signature"];

function toPayload(f) {
  const p = {};
  TEXT_KEYS.forEach((k) => { p[k] = f[k] ?? ""; });
  p.total_hours = f.total_hours !== "" && f.total_hours != null ? Number(f.total_hours) : null;
  p.crew_size = f.crew_size !== "" && f.crew_size != null ? parseInt(f.crew_size, 10) : null;
  p.crew_member_ids = f.crew_member_ids || [];
  p.line_values = Object.fromEntries(Object.entries(f.line_values || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)]));
  return p;
}

const Step = ({ n, title, children, testId }) => (
  <section className="panel p-4 sm:p-5" data-testid={testId}>
    <div className="mb-4 flex items-center gap-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#5C241E] font-display text-base font-bold text-white">{n}</span>
      <h2 className="font-display text-2xl font-semibold text-slate-900">{title}</h2>
    </div>
    {children}
  </section>
);

export default function ReportForm({ admin }) {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, meta } = useAuth();
  const base = admin ? "/admin/reports" : "/reports";
  const [opts, setOpts] = useState(null);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("draft");
  const [photos, setPhotos] = useState([]);
  const [prev, setPrev] = useState({ previous: {}, total_footage: 0 });
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState("");
  const [pending, setPending] = useState(0);
  const rid = useRef(id || null);
  const creating = useRef(null);
  const adopted = useRef(false);
  const localKey = useRef(`qdb-report-${id || "new"}-${user.id}`);

  useEffect(() => {
    (async () => {
      const o = (await api.get("/form-options")).data;
      let initial;
      let updatedAt = "";
      if (id) {
        let d;
        try { d = (await api.get(`/reports/${id}`)).data; } catch (e) { toast.error(errMsg(e)); return nav(base === "/reports" ? "/history" : base); }
        if (!d.can_edit) { toast.error("This report can no longer be edited"); return nav(`${base}/${id}`, { replace: true }); }
        const r = d.report;
        if (!o.projects.find((p) => p.id === d.project.id)) o.projects.push({ ...d.project, line_items: (d.project.line_items || []).filter((i) => i.enabled !== false) });
        if (!o.clients.find((c) => c.id === d.client.id)) o.clients.push({ id: d.client.id, name: d.client.name });
        initial = { ...r, total_hours: r.total_hours ?? "", crew_size: r.crew_size ?? "", crew_member_ids: r.crew_member_ids || [], line_values: r.line_values || {} };
        TEXT_KEYS.forEach((k) => { initial[k] = r[k] ?? ""; });
        setStatus(r.status);
        setPhotos(d.photos);
        updatedAt = r.updated_at;
      } else {
        initial = { report_date: meta.today, client_id: "", project_id: "", contractor: "", pipe_description: "", weather: "", start_time: "", end_time: "", total_hours: "", crew_size: "", crew_member_ids: [], line_values: {}, comments: "", issues: "", safety: meta.default_safety || "", signature: `${user.name}    ${meta.signature_suffix || ""}` };
      }
      try {
        const saved = JSON.parse(localStorage.getItem(localKey.current) || "null");
        if (saved && saved.savedAt > (updatedAt || "")) {
          initial = { ...initial, ...saved.form };
          setDirty(true);
          toast.info("Restored your unsaved changes on this device");
        }
      } catch { /* ignore */ }
      setOpts(o);
      setForm(initial);
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form && dirty) localStorage.setItem(localKey.current, JSON.stringify({ form, savedAt: new Date().toISOString() }));
  }, [form, dirty]);

  useEffect(() => {
    const h = (e) => { if (dirty || pending) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty, pending]);

  useEffect(() => {
    if (!form?.project_id || !form?.report_date) return;
    api.get("/reports/previous", { params: { project_id: form.project_id, report_date: form.report_date, report_id: rid.current || undefined } })
      .then((r) => setPrev(r.data)).catch(() => {});
  }, [form?.project_id, form?.report_date]);

  const set = (k, v) => {
    setDirty(true);
    setErrors((e) => ({ ...e, [k]: undefined }));
    setForm((f) => {
      const n = { ...f, [k]: v };
      if (k === "start_time" || k === "end_time") { const h = hoursBetween(n.start_time, n.end_time); if (h !== "") n.total_hours = h; }
      return n;
    });
  };

  const selectProject = (pid) => {
    const p = opts.projects.find((x) => x.id === pid);
    setDirty(true);
    setErrors((e) => ({ ...e, project_id: undefined, client_id: undefined }));
    setForm((f) => ({ ...f, project_id: pid, client_id: p ? p.client_id : f.client_id,
      contractor: f.contractor || p?.contractor || "", pipe_description: f.pipe_description || p?.pipe_description || "" }));
  };

  const clearLocal = () => { localStorage.removeItem(localKey.current); setDirty(false); };

  const handleDup = (e) => {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 409 && d?.existing_id) {
      toast.error(d.message, { action: { label: "Open it", onClick: () => nav(`${base}/${d.existing_id}/edit`) }, duration: 8000 });
      return true;
    }
    return false;
  };

  const adopt = async (existingId) => {
    let d;
    try { d = (await api.get(`/reports/${existingId}`)).data; } catch { return null; }
    if (!d.can_edit) {
      toast.error("You already submitted a report for this project and date, and it can no longer be edited. Ask the office to correct it.", { duration: 9000 });
      return null;
    }
    localStorage.removeItem(localKey.current);
    rid.current = existingId;
    adopted.current = true;
    localKey.current = `qdb-report-${existingId}-${user.id}`;
    window.history.replaceState(null, "", `${base}/${existingId}/edit`);
    setStatus(d.report.status);
    setPhotos((p) => [...d.photos, ...p.filter((x) => !d.photos.some((y) => y.id === x.id))]);
    toast.info("You already had a report for this project and date — your changes are being saved to it.");
    return existingId;
  };

  const ensureReport = useCallback(async () => {
    if (rid.current) return rid.current;
    if (!form.project_id || !form.report_date) {
      setErrors((e) => ({ ...e, project_id: form.project_id ? undefined : "Select a project first", report_date: form.report_date ? undefined : "Date is required" }));
      toast.error("Select the project and date first");
      return null;
    }
    if (!creating.current) {
      creating.current = api.post("/reports", toPayload(form)).then(({ data }) => {
        localStorage.removeItem(localKey.current);
        rid.current = data.id;
        localKey.current = `qdb-report-${data.id}-${user.id}`;
        window.history.replaceState(null, "", `${base}/${data.id}/edit`);
        return data.id;
      }).catch((e) => {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 409 && d?.existing_id) return adopt(d.existing_id);
        setErrors(fieldErrors(e)); toast.error(errMsg(e));
        return null;
      }).finally(() => { creating.current = null; });
    }
    return creating.current;
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = async () => {
    setSaving("draft");
    try {
      const existed = !!rid.current;
      const r = await ensureReport();
      if (!r) return;
      if (existed || adopted.current) await api.put(`/reports/${r}`, toPayload(form));
      adopted.current = false;
      clearLocal();
      toast.success(status === "draft" ? "Draft saved" : "Changes saved");
      if (status !== "draft") nav(`${base}/${r}`);
    } catch (e) {
      if (!handleDup(e)) { setErrors(fieldErrors(e)); toast.error(errMsg(e)); }
    } finally {
      setSaving("");
    }
  };

  const submit = async () => {
    const errs = {};
    if (!form.report_date) errs.report_date = "Date is required";
    if (!form.client_id) errs.client_id = "Client is required";
    if (!form.project_id) errs.project_id = "Project is required";
    if (!form.comments?.trim()) errs.comments = "Describe the work performed today";
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please complete the required fields");
      document.querySelector(`[data-testid="report-${Object.keys(errs)[0].replace("_id", "")}-input"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (pending) return toast.error("Wait for photos to finish uploading");
    setSaving("submit");
    try {
      const r = await ensureReport();
      if (!r) return;
      await api.post(`/reports/${r}/submit`, toPayload(form));
      clearLocal();
      nav(`${base}/${r}`, { state: { justSubmitted: status === "draft" } });
    } catch (e) {
      if (!handleDup(e)) { setErrors(fieldErrors(e)); toast.error(errMsg(e)); }
    } finally {
      setSaving("");
    }
  };

  if (!form || !opts) return <LoadingBlock />;
  const project = opts.projects.find((p) => p.id === form.project_id);
  const clientProjects = opts.projects.filter((p) => !form.client_id || p.client_id === form.client_id);
  const isDraft = status === "draft";

  return (
    <div className="space-y-4 pb-24 fade-up">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="eyebrow">Inspectors Daily Progress Report</p>
          <h1 className="mt-1 text-3xl font-bold sm:text-4xl" data-testid="report-form-title">{id || rid.current ? (isDraft ? "Draft report" : "Edit report") : "New daily report"}</h1>
        </div>
        <StatusBadge status={status} testId="report-form-status" />
      </div>
      {dirty && <p className="flex items-center gap-2 text-xs text-slate-500" data-testid="local-autosave-note"><CloudOff className="h-3.5 w-3.5" />Changes are kept on this device until you save or submit.</p>}

      <Step n="1" title="Job information" testId="step-job-info">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Report date" required error={errors.report_date} htmlFor="report_date">
            <input id="report_date" type="date" className="field-input" value={form.report_date} onChange={(e) => set("report_date", e.target.value)} data-testid="report-report_date-input" />
          </Field>
          <Field label="Client" required error={errors.client_id} htmlFor="client">
            <NativeSelect id="client" value={form.client_id} error={errors.client_id} onChange={(e) => { set("client_id", e.target.value); if (project && project.client_id !== e.target.value) set("project_id", ""); }} data-testid="report-client-input">
              <option value="">Select client…</option>
              {opts.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </NativeSelect>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Project / job" required error={errors.project_id} htmlFor="project">
              <NativeSelect id="project" value={form.project_id} error={errors.project_id} onChange={(e) => selectProject(e.target.value)} data-testid="report-project-input">
                <option value="">Select project…</option>
                {clientProjects.map((p) => <option key={p.id} value={p.id}>{p.name}{p.job_number ? ` — Job # ${p.job_number}` : ""}</option>)}
              </NativeSelect>
            </Field>
            {project && <p className="mt-1.5 text-xs text-slate-500" data-testid="report-project-meta">Location: {project.location || "—"}{project.template === "facility" ? "" : ` · Total pipeline footage: ${Number(project.total_footage || 0).toLocaleString()}`}</p>}
          </div>
          <Field label="Contractor"><input className="field-input" value={form.contractor} onChange={(e) => set("contractor", e.target.value)} data-testid="report-contractor-input" /></Field>
          <Field label={project?.template === "facility" ? "Job description" : "Pipe description"}><input className="field-input" value={form.pipe_description} onChange={(e) => set("pipe_description", e.target.value)} data-testid="report-pipe-input" /></Field>
          <Field label="Weather"><input className="field-input" placeholder="e.g. Mostly Sunny / 97°" value={form.weather} onChange={(e) => set("weather", e.target.value)} data-testid="report-weather-input" /></Field>
          <Field label="Crew size"><input type="text" inputMode="numeric" className="field-input" value={form.crew_size} onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 3); set("crew_size", v); const n = Number(v || 0); if ((form.crew_member_ids || []).length > n) set("crew_member_ids", form.crew_member_ids.slice(0, n)); }} data-testid="report-crew-input" /></Field>
          <CrewPicker options={opts.crew || []} size={form.crew_size} selected={form.crew_member_ids || []} onChange={(ids) => set("crew_member_ids", ids)} />
          <div className="grid grid-cols-3 gap-2 sm:col-span-2">
            <Field label="Start" error={errors.start_time}><input type="time" className="field-input px-2" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="report-start-input" /></Field>
            <Field label="End" error={errors.end_time}><input type="time" className="field-input px-2" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} data-testid="report-end-input" /></Field>
            <Field label="Hours" error={errors.total_hours}><input type="text" inputMode="decimal" className="field-input font-mono-num px-2" value={form.total_hours} onChange={(e) => set("total_hours", cleanNum(e.target.value))} data-testid="report-hours-input" /></Field>
          </div>
        </div>
      </Step>

      <Step n="2" title="Today's footage & counts" testId="step-footage">
        {project ? (
          <FootageInputs template={project.template} items={project.line_items || []} values={form.line_values} previous={prev.previous} totalFootage={Number(prev.total_footage || project.total_footage || 0)}
            onChange={(k, v) => set("line_values", { ...form.line_values, [k]: v })} errors={errors} />
        ) : <p className="text-sm text-slate-500">Select a project to load its line items.</p>}
      </Step>

      <Step n="3" title="Comments & safety" testId="step-comments">
        <div className="space-y-4">
          <Field label="Work performed / comments" required error={errors.comments} htmlFor="comments">
            <textarea id="comments" rows={6} className="field-area" placeholder="What did the contractor accomplish today? Areas worked, equipment, materials, progress…" value={form.comments} onChange={(e) => set("comments", e.target.value)} data-testid="report-comments-input" />
          </Field>
          <Field label="Delays / issues encountered" hint="Optional — flagged for the office">
            <textarea rows={3} className="field-area" value={form.issues} onChange={(e) => set("issues", e.target.value)} data-testid="report-issues-input" />
          </Field>
          <Field label="Safety">
            <textarea rows={4} className="field-area text-sm" value={form.safety} onChange={(e) => set("safety", e.target.value)} data-testid="report-safety-input" />
          </Field>
          <Field label="Inspector signature" hint="Typed name as it appears on the report">
            <input className="field-input" value={form.signature} onChange={(e) => set("signature", e.target.value)} data-testid="report-signature-input" />
          </Field>
        </div>
      </Step>

      <Step n="4" title="Jobsite photos" testId="step-photos">
        <PhotoUploader ensureReport={ensureReport} photos={photos} setPhotos={setPhotos} onPendingChange={setPending} maxMb={meta.max_photo_mb} disabled={!form.project_id || !form.report_date} />
      </Step>

      {createPortal(<div className={`pb-safe fixed inset-x-0 z-30 border-t border-border bg-white/95 backdrop-blur ${admin ? "bottom-0 lg:left-64" : "bottom-16 md:bottom-0"}`} data-testid="report-action-bar">
        <div className="mx-auto grid max-w-3xl grid-cols-2 gap-2 px-4 py-3">
          <button type="button" onClick={saveDraft} disabled={!!saving} className="btn-ghost h-14 text-base" data-testid="report-save-draft-button">
            {saving === "draft" ? <Spinner /> : <Save className="h-5 w-5" />}{isDraft ? "Save Draft" : "Save Changes"}
          </button>
          {isDraft && (
            <button type="button" onClick={submit} disabled={!!saving || pending > 0} className="btn-brand h-14 text-base" data-testid="report-submit-button">
              {saving === "submit" ? <Spinner className="text-white" /> : <Send className="h-5 w-5" />}{pending ? "Uploading…" : "Submit Report"}
            </button>
          )}
          {!isDraft && (
            <button type="button" onClick={() => nav(`${base}/${rid.current}`)} className="btn-ghost h-14 text-base" data-testid="report-cancel-edit-button">Cancel</button>
          )}
        </div>
      </div>, document.body)}
    </div>
  );
}
